import { useRef, useState } from 'react'
import {
  AlertTriangle, CheckCircle2, FileCheck2, UploadCloud, X, XCircle,
} from 'lucide-react'

import { api } from '@/api/client'
import { GroupPicker } from '@/components/GroupPicker'
import { useToast } from '@/stores/toast'
import { cn } from '@/lib/utils'

type DocType = 'po' | 'quote'

// 백엔드 verify 응답 라인 (발주/견적 공용).
interface VerifyLine {
  품목: string
  코드: string
  수량?: number | string
  단가?: number | string
  매출처: string
  상태: string  // saved | not_found | no_item | no_price | no_route | qty_mismatch | price_mismatch
  비고?: string
  DB수량?: string
  DB단가?: string
}

interface VerifyReport {
  ok: boolean
  error?: string
  doc_type?: string       // "발주서" | "견적서"
  원본파일명?: string
  상위거래처?: string
  출고일자?: string
  적용월?: string
  headline?: string
  counts?: Record<string, number>
  total_lines?: number
  saved?: number
  전표번호?: string[]
  미매칭_부서?: Record<string, number>
  lines?: VerifyLine[]
}

const STATUS_META: Record<string, { label: string; cls: string }> = {
  saved:         { label: '저장됨',           cls: 'bg-emerald-100 text-emerald-800' },
  not_found:     { label: 'DB에 없음(미저장)', cls: 'bg-rose-100 text-rose-800' },
  no_item:       { label: '품목 미등록',      cls: 'bg-amber-100 text-amber-800' },
  no_price:      { label: '단가 없음',        cls: 'bg-amber-100 text-amber-800' },
  no_route:      { label: '라우팅 실패',      cls: 'bg-orange-100 text-orange-800' },
  qty_mismatch:  { label: '수량 불일치',      cls: 'bg-purple-100 text-purple-800' },
  price_mismatch:{ label: '단가 불일치',      cls: 'bg-purple-100 text-purple-800' },
}

const DOC_META: Record<DocType, { label: string; accept: string; hint: string; savedWord: string }> = {
  po:    { label: '발주서', accept: '.xlsx,.xls,.html,.pdf', hint: '.xlsx / .xls / .html / .pdf', savedWord: '출고전표' },
  quote: { label: '견적서', accept: '.xls,.xlsx,.xlsb',       hint: '.xls / .xlsx / .xlsb',        savedWord: '단가' },
}

type Filter = 'all' | 'problem' | string

export function VerifyTab() {
  const push = useToast((s) => s.push)
  const [docType, setDocType] = useState<DocType>('po')
  const [groupId, setGroupId] = useState<number | null>(null)
  const [groupLabel, setGroupLabel] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [report, setReport] = useState<VerifyReport | null>(null)
  const [loading, setLoading] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [filter, setFilter] = useState<Filter>('all')
  const picker = useRef<HTMLInputElement>(null)

  const canUpload = !!groupId
  const isQuote = report?.doc_type === '견적서'

  async function runVerify(f: File) {
    if (!groupId) return
    setLoading(true); setReport(null); setFilter('all')
    try {
      const fd = new FormData()
      fd.append('doc_type', docType)
      fd.append('group_id', String(groupId))
      fd.append('file', f)
      const r = await api<VerifyReport>('/api/verify', { method: 'POST', body: fd })
      setReport(r)
    } catch (e) {
      setReport({ ok: false, error: (e as Error).message })
    } finally {
      setLoading(false)
    }
  }

  function handleFiles(list: FileList | null) {
    if (!canUpload) return
    if (!list || !list.length) return
    if (list.length > 1) { push('한 번에 한 개 파일만 검증할 수 있어요.', 'warn'); return }
    setFile(list[0]); runVerify(list[0])
  }

  function switchDoc(t: DocType) {
    setDocType(t); setFile(null); setReport(null)
  }

  const ok = report?.ok
  const allSaved = ok && (report?.total_lines ?? 0) > 0 && report?.saved === report?.total_lines
  const lines = report?.lines ?? []
  const shown = lines.filter((l) => {
    if (filter === 'all') return true
    if (filter === 'problem') return l.상태 !== 'saved'
    return l.상태 === filter
  })
  const dm = DOC_META[docType]

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-4">
        <h1 className="text-lg font-bold text-slate-800 flex items-center gap-2">
          <FileCheck2 className="w-5 h-5 text-emerald-600" /> 파일 저장 검증
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">
          업로드한 발주서/견적서가 DB에 제대로 저장됐는지 줄 단위로 대조합니다. (저장 안 함 · 조회 전용)
        </p>
      </div>

      {/* 문서 종류 토글 */}
      <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 mb-4">
        {(['po', 'quote'] as DocType[]).map((t) => (
          <button key={t} onClick={() => switchDoc(t)}
                  className={cn(
                    'px-4 py-1.5 rounded-md text-sm font-semibold transition',
                    docType === t ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-800',
                  )}>
            {DOC_META[t].label}
          </button>
        ))}
      </div>

      {/* 상위 거래처 */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
          상위 거래처 *
        </label>
        <div className="w-96">
          <GroupPicker
            value={groupId}
            onChange={(g) => { setGroupId(g.id); setGroupLabel(`${g.name} · ${g.member_count}개`) }}
            placeholder={groupLabel || '상위 거래처 선택 (필수)'}
          />
        </div>
      </div>

      {/* 파일 업로드 (드래그앤드롭, 단일) */}
      <div
        role="button" tabIndex={canUpload ? 0 : -1}
        onClick={() => canUpload && picker.current?.click()}
        onKeyDown={(e) => { if (canUpload && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); picker.current?.click() } }}
        className={cn(
          'block rounded-2xl border-2 border-dashed px-8 py-10 text-center bg-white transition-colors focus:outline-none mb-4',
          !canUpload
            ? 'border-slate-200 opacity-50 cursor-not-allowed'
            : dragging
              ? 'border-emerald-500 bg-emerald-50 cursor-pointer'
              : 'border-slate-300 hover:border-emerald-400 focus:border-emerald-500 cursor-pointer',
        )}
        onDrop={(e) => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files) }}
        onDragOver={(e) => { e.preventDefault(); if (canUpload) setDragging(true) }}
        onDragLeave={(e) => { e.preventDefault(); setDragging(false) }}
      >
        <UploadCloud className="mx-auto w-10 h-10 text-slate-400" />
        <p className="mt-2 text-base font-semibold text-slate-700">
          {canUpload ? `${dm.label}를 여기로 드래그하거나 클릭해서 선택` : '상위 거래처를 먼저 선택하세요'}
        </p>
        <p className="text-sm text-slate-500 mt-1">{dm.label} 파일 한 개 ({dm.hint})</p>
        <input
          ref={picker} type="file"
          style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
          accept={dm.accept}
          onChange={(e) => { handleFiles(e.target.files); e.target.value = '' }}
        />
      </div>

      {file && (
        <div className="mb-4 flex items-center gap-2">
          <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-lg pl-3 pr-2 py-1.5 text-sm shadow-sm">
            <span className="font-medium text-slate-700 truncate max-w-[280px]">{file.name}</span>
            <span className="text-xs text-slate-400 tabular-nums">{(file.size / 1024).toFixed(0)} KB</span>
            <button onClick={() => { setFile(null); setReport(null) }}
                    className="text-slate-400 hover:text-rose-500 p-0.5 rounded" title="지우기">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          {loading && <span className="text-sm text-slate-500">검증 중…</span>}
        </div>
      )}

      {/* 파싱/오류 */}
      {report && !ok && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 flex items-start gap-2">
          <XCircle className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-rose-800">검증할 수 없습니다</div>
            <div className="text-sm text-rose-700 mt-0.5">{report.error}</div>
          </div>
        </div>
      )}

      {/* 결과 */}
      {report && ok && (
        <>
          <div className={cn(
            'rounded-xl p-4 mb-4 flex items-start gap-3 border',
            allSaved ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200',
          )}>
            {allSaved
              ? <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
              : <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0" />}
            <div className="flex-1">
              <div className={cn('font-bold', allSaved ? 'text-emerald-800' : 'text-amber-800')}>
                {report.headline}
              </div>
              <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
                <span>상위: <b>{report.상위거래처}</b></span>
                {isQuote
                  ? <span>적용월: <b>{report.적용월}</b></span>
                  : <>
                      <span>출고일자: <b>{report.출고일자 || '—'}</b></span>
                      <span>전표 {report.전표번호?.length ?? 0}건</span>
                    </>}
                <span>총 {report.total_lines}줄</span>
              </div>
            </div>
          </div>

          {/* 상태 필터 칩 */}
          <div className="flex flex-wrap gap-2 mb-3">
            <Chip active={filter === 'all'} onClick={() => setFilter('all')}
                  label={`전체 ${report.total_lines}`} cls="bg-slate-100 text-slate-700" />
            {(report.total_lines ?? 0) - (report.saved ?? 0) > 0 && (
              <Chip active={filter === 'problem'} onClick={() => setFilter('problem')}
                    label={`문제 ${(report.total_lines ?? 0) - (report.saved ?? 0)}`}
                    cls="bg-rose-100 text-rose-700" />
            )}
            {Object.entries(countByStatus(lines)).map(([st, n]) => (
              <Chip key={st} active={filter === st} onClick={() => setFilter(st)}
                    label={`${STATUS_META[st]?.label ?? st} ${n}`}
                    cls={STATUS_META[st]?.cls ?? 'bg-slate-100 text-slate-700'} />
            ))}
          </div>

          {/* 라인 표 */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">상태</th>
                    <th className="text-left px-3 py-2 font-semibold">품목</th>
                    <th className="text-left px-3 py-2 font-semibold">코드</th>
                    <th className="text-right px-3 py-2 font-semibold">{isQuote ? '단가' : '수량'}</th>
                    <th className="text-left px-3 py-2 font-semibold">매출처</th>
                    <th className="text-left px-3 py-2 font-semibold">비고</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {shown.map((l, i) => {
                    const m = STATUS_META[l.상태] ?? { label: l.상태, cls: 'bg-slate-100 text-slate-700' }
                    const val = isQuote ? l.단가 : l.수량
                    const dbVal = isQuote ? l.DB단가 : l.DB수량
                    return (
                      <tr key={i} className={l.상태 === 'saved' ? '' : 'bg-rose-50/30'}>
                        <td className="px-3 py-1.5">
                          <span className={cn('inline-block px-2 py-0.5 rounded text-xs font-semibold', m.cls)}>
                            {m.label}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-slate-700">{l.품목}</td>
                        <td className="px-3 py-1.5 text-slate-400 tabular-nums text-xs">{l.코드}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">
                          {val}
                          {dbVal !== undefined && (
                            <span className="text-purple-600 text-xs"> (DB {dbVal})</span>
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-slate-600">{l.매출처}</td>
                        <td className="px-3 py-1.5 text-slate-400 text-xs">{l.비고 ?? ''}</td>
                      </tr>
                    )
                  })}
                  {!shown.length && (
                    <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">해당 라인 없음</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function Chip({ active, onClick, label, cls }: {
  active: boolean; onClick: () => void; label: string; cls: string
}) {
  return (
    <button onClick={onClick}
            className={cn(
              'px-2.5 py-1 rounded-lg text-xs font-semibold transition',
              active ? `${cls} ring-2 ring-offset-1 ring-slate-300` : `${cls} opacity-70 hover:opacity-100`,
            )}>
      {label}
    </button>
  )
}

function countByStatus(lines: VerifyLine[]): Record<string, number> {
  const c: Record<string, number> = {}
  for (const l of lines) c[l.상태] = (c[l.상태] ?? 0) + 1
  return c
}
